import { useT, type Translate } from "../../../../shared/i18n/i18n";
import { DndSortableVerticalList, useDndSortableItem } from "../../../../shared/ui/multi-container-dnd";
import { Archive, Check, Flag, GripVertical, Plus, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import type { BoardStatusCategory, BoardStatusDefinition, BoardView } from "../../../../entities/board/model/use-boards";
import { useWorkObjectFields } from "../../../../entities/work-object-field/model/use-work-object-fields";
import { boardStatusCategories, boardStatusColorPresets, defaultStatusColor } from "../../../../entities/board/model/board-statuses";
import { EditableText } from "../../../../shared/ui/editable-text";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { StatusEntryRuleDialog } from "./status-entry-rule-dialog";

type BoardStatusManagerDialogProps = {
  board: BoardView;
  disabled: boolean;
  onClearStatusEntryRule: (input: { boardId: string; targetStatusId: string }) => Promise<void>;
  onArchiveStatus: (input: { boardId: string; boardStatusId: string; replacementStatusId?: string | null }) => Promise<void>;
  onSetDefaultStatus: (input: { boardId: string; boardStatusId: string }) => Promise<void>;
  onCreateStatus: (input: { boardId: string; category: BoardStatusCategory; color: string; name: string }) => Promise<void>;
  onMoveStatus: (input: { boardId: string; boardStatusId: string; nextStatusId?: string | null; previousStatusId?: string | null }) => Promise<void>;
  onOpenChange: (open: boolean) => void;
  onUpdateStatus: (input: { boardId: string; boardStatusId: string; color: string; name: string }) => Promise<void>;
  onUpdateStatusEntryRule: (input: { boardId: string; expression: string; targetStatusId: string }) => Promise<void>;
  open: boolean;
};

export function BoardStatusManagerDialog({
  board,
  disabled,
  onClearStatusEntryRule,
  onArchiveStatus,
  onCreateStatus,
  onMoveStatus,
  onOpenChange,
  onSetDefaultStatus,
  onUpdateStatus,
  onUpdateStatusEntryRule,
  open,
}: BoardStatusManagerDialogProps) {
  const t = useT();
  const fields = useWorkObjectFields();
  const [ruleStatus, setRuleStatus] = useState<BoardStatusDefinition | null>(null);
  async function handleReorder(category: BoardStatusCategory, itemId: string, orderedItemIds: string[]) {
    if (category === "closed") {
      return;
    }
    const targetIndex = orderedItemIds.indexOf(itemId);
    if (targetIndex < 0) {
      return;
    }

    await onMoveStatus({
      boardId: board.id,
      boardStatusId: itemId,
      nextStatusId: orderedItemIds[targetIndex + 1] ?? null,
      previousStatusId: orderedItemIds[targetIndex - 1] ?? null,
    });
  }

  return (
    <>
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("statuses.title")}</DialogTitle>
            <DialogDescription>Status is board-specific and can be used by any view, even when the current view is grouped by another field.</DialogDescription>
          </DialogHeader>
          <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
            {boardStatusCategories.map((category) => (
              <BoardStatusCategorySection
                board={board}
                category={category.id}
                disabled={disabled}
                key={category.id}
                onArchiveStatus={onArchiveStatus}
                onCreateStatus={onCreateStatus}
                onConfigureRule={setRuleStatus}
                onReorder={(itemId, orderedItemIds) => void handleReorder(category.id, itemId, orderedItemIds)}
                onSetDefaultStatus={onSetDefaultStatus}
                onUpdateStatus={onUpdateStatus}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <StatusEntryRuleDialog
        board={board}
        disabled={disabled}
        fields={fields.data}
        onClear={onClearStatusEntryRule}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setRuleStatus(null);
        }}
        onSave={onUpdateStatusEntryRule}
        open={ruleStatus !== null}
        status={ruleStatus}
      />
    </>
  );
}

function BoardStatusCategorySection({
  board,
  category,
  disabled,
  onArchiveStatus,
  onCreateStatus,
  onConfigureRule,
  onReorder,
  onSetDefaultStatus,
  onUpdateStatus,
}: {
  board: BoardView;
  category: BoardStatusCategory;
  disabled: boolean;
  onArchiveStatus: BoardStatusManagerDialogProps["onArchiveStatus"];
  onCreateStatus: BoardStatusManagerDialogProps["onCreateStatus"];
  onConfigureRule: (status: BoardStatusDefinition) => void;
  onReorder: (itemId: string, orderedItemIds: string[]) => void;
  onSetDefaultStatus: BoardStatusManagerDialogProps["onSetDefaultStatus"];
  onUpdateStatus: BoardStatusManagerDialogProps["onUpdateStatus"];
}) {
  const t = useT();
  const categoryMeta = boardStatusCategories.find((item) => item.id === category);
  const isClosedCategory = category === "closed";
  const statuses = useMemo(() => statusesForCategory(board.statuses, category), [board.statuses, category]);

  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-medium">{categoryMeta ? t(categoryMeta.labelKey) : category}</h3>
          <p className="text-xs text-muted-foreground">{categoryMeta && t(categoryMeta.descriptionKey)}</p>
        </div>
      </div>
      <div className="flex flex-col gap-1">
        {statuses.length > 0 ? (
          <DndSortableVerticalList
            itemIds={statuses.map((status) => status.id)}
            onReorder={({ itemId, orderedItemIds }) => onReorder(itemId, orderedItemIds)}
          >
            <div className="flex flex-col gap-1">
              {statuses.map((status) => (
                <BoardStatusRow
                  board={board}
                  disabled={disabled}
                  key={status.id}
                  onArchiveStatus={onArchiveStatus}
                  onConfigureRule={onConfigureRule}
                  onSetDefaultStatus={onSetDefaultStatus}
                  onUpdateStatus={onUpdateStatus}
                  status={status}
                />
              ))}
            </div>
          </DndSortableVerticalList>
        ) : null}
        {isClosedCategory ? null : (
          <CreateStatusInline
            category={category}
            disabled={disabled}
            onCreate={(name) =>
              onCreateStatus({
                boardId: board.id,
                category,
                color: defaultStatusColor(category),
                name,
              })
            }
          />
        )}
      </div>
    </section>
  );
}

function BoardStatusRow({
  board,
  disabled,
  onArchiveStatus,
  onConfigureRule,
  onSetDefaultStatus,
  onUpdateStatus,
  status,
}: {
  board: BoardView;
  disabled: boolean;
  onArchiveStatus: BoardStatusManagerDialogProps["onArchiveStatus"];
  onConfigureRule: (status: BoardStatusDefinition) => void;
  onSetDefaultStatus: BoardStatusManagerDialogProps["onSetDefaultStatus"];
  onUpdateStatus: BoardStatusManagerDialogProps["onUpdateStatus"];
  status: BoardStatusDefinition;
}) {
  const t = useT();
  const isClosedStatus = status.category === "closed";
  // Where new work lands. Kept apart from column order so rearranging the
  // board never changes where work starts.
  const isDefaultStatus = board.defaultStatusId === status.id;
  const canBeDefault = status.category !== "closed" && status.category !== "done";
  const sortable = useDndSortableItem(status.id, { disabled: disabled || isClosedStatus });
  const categoryStatusCount = board.statuses.filter((item) => item.category === status.category).length;
  const canArchiveByCategory = status.category === "done" || categoryStatusCount > 1;
  const workItemCount = board.items.filter((item) => item.status.id === status.id).length;
  const replacementStatuses = board.statuses.filter((item) => item.id !== status.id);
  const [selectedReplacementStatusId, setSelectedReplacementStatusId] = useState<string | null>(null);
  const [isArchiveOpen, setIsArchiveOpen] = useState(false);
  const replacementStatusId = replacementStatuses.some((item) => item.id === selectedReplacementStatusId)
    ? selectedReplacementStatusId
    : (replacementStatuses[0]?.id ?? "");
  const needsReplacement = workItemCount > 0;
  const canArchive = !disabled && canArchiveByCategory && (!needsReplacement || Boolean(replacementStatusId));
  const archiveDisabledReason = archiveStatusDisabledReason({
    canArchiveByCategory,
    disabled,
    status,
    t,
  });

  return (
    <>
      <div
        className={cn(
          "flex h-9 w-full min-w-0 items-center gap-1.5 rounded-md border border-border/55 bg-muted/15 px-1.5 transition-colors hover:bg-muted/30",
          sortable.isDragSource && "relative z-10 opacity-75",
        )}
        ref={sortable.setNodeRef}
        style={sortable.style}
      >
        {isClosedStatus ? (
          <>
            <span className="size-5" />
            <span className="size-3.5 rounded-sm border border-border/70" style={{ backgroundColor: status.color }} />
          </>
        ) : (
          <>
            <Button
              aria-label={t("a11y.drag", { name: status.name })}
              className="cursor-grab touch-none active:cursor-grabbing"
              disabled={disabled}
              size="icon-xs"
              type="button"
              variant="ghost"
              {...sortable.dragProps}
            >
              <GripVertical />
            </Button>
            <StatusColorPicker
              disabled={disabled}
              value={status.color}
              onChange={(color) => onUpdateStatus({ boardId: board.id, boardStatusId: status.id, color, name: status.name })}
            />
          </>
        )}
        <EditableText
          ariaLabel={t("a11y.editStatusName", { name: status.name })}
          className="min-w-0 flex-1"
          onChange={(name) => {
            if (name.trim() && name !== status.name) {
              void onUpdateStatus({ boardId: board.id, boardStatusId: status.id, color: status.color, name });
            }
          }}
          placeholder={t("statuses.status")}
          previewClassName="text-xs font-medium leading-5"
          value={status.name}
        />
        {isDefaultStatus ? (
          <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">{t("statuses.default")}</span>
        ) : canBeDefault ? (
          <Button
            aria-label={t("a11y.makeDefaultStatus", { name: status.name })}
            disabled={disabled}
            onClick={() => void onSetDefaultStatus({ boardId: board.id, boardStatusId: status.id })}
            size="sm"
            type="button"
            variant="ghost"
          >
            <Flag data-icon="inline-start" />
            {t("statuses.makeDefault")}
          </Button>
        ) : null}
        <Button
          aria-label={t("a11y.statusRequirements", { name: status.name })}
          onClick={() => onConfigureRule(status)}
          size="sm"
          type="button"
          variant={status.entryRule ? "secondary" : "ghost"}
        >
          <ShieldCheck data-icon="inline-start" />
          {status.entryRule ? t("statuses.rules") : t("statuses.addRules")}
        </Button>
        <ArchiveStatusButton
          disabled={Boolean(archiveDisabledReason)}
          onClick={() => setIsArchiveOpen(true)}
          reason={archiveDisabledReason}
          statusName={status.name}
        />
      </div>
      <AlertDialog onOpenChange={setIsArchiveOpen} open={isArchiveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive "{status.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              {needsReplacement
                ? `${workItemCount} work item${workItemCount === 1 ? "" : "s"} will move to another status before this status is archived.`
                : "The status will leave this board's active status list."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {needsReplacement ? (
            <div className="grid gap-1.5">
              <label className="text-xs font-medium" htmlFor={`replacement-status-${status.id}`}>
                {t("statuses.moveItemsTo")}
              </label>
              <Select
                onValueChange={(value) => {
                  if (value !== null) {
                    setSelectedReplacementStatusId(value);
                  }
                }}
                value={replacementStatusId ?? ""}
              >
                <SelectTrigger className="w-full" id={`replacement-status-${status.id}`}>
                  <SelectValue placeholder={t("statuses.selectStatus")}>
                    {(value: string | null) => {
                      const selectedStatus = replacementStatuses.find((item) => item.id === value);
                      return selectedStatus ? (
                        <>
                          <span className="size-2.5 shrink-0 rounded-sm" style={{ backgroundColor: selectedStatus.color }} />
                          <span className="truncate">{selectedStatus.name}</span>
                        </>
                      ) : ( t("statuses.selectStatus")
                      );
                    }}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent align="start">
                  {replacementStatuses.map((replacementStatus) => (
                    <SelectItem key={replacementStatus.id} value={replacementStatus.id}>
                      <span className="size-2.5 rounded-sm" style={{ backgroundColor: replacementStatus.color }} />
                      {replacementStatus.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={disabled}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              disabled={!canArchive}
              onClick={() => {
                void onArchiveStatus({
                  boardId: board.id,
                  boardStatusId: status.id,
                  replacementStatusId: needsReplacement ? replacementStatusId : null,
                });
              }}
              variant="destructive"
            >
              {t("statuses.archive")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ArchiveStatusButton({
  disabled,
  onClick,
  reason,
  statusName,
}: {
  disabled: boolean;
  onClick: () => void;
  reason?: string | null;
  statusName: string;
}) {
  const t = useT();
  const button = (
    <Button
      aria-label={t("a11y.archive", { name: statusName })}
      disabled={disabled}
      onClick={onClick}
      size="icon-xs"
      type="button"
      variant="ghost"
    >
      <Archive />
    </Button>
  );

  if (!reason) {
    return button;
  }

  return (
    <Tooltip>
      <TooltipTrigger render={<span className="inline-flex size-5 items-center justify-center">{button}</span>} />
      <TooltipContent>{reason}</TooltipContent>
    </Tooltip>
  );
}

function archiveStatusDisabledReason({
  canArchiveByCategory,
  disabled,
  status,
  t,
}: {
  canArchiveByCategory: boolean;
  disabled: boolean;
  status: BoardStatusDefinition;
  t: Translate;
}) {
  if (disabled) {
    return t("statuses.savingChanges");
  }
  if (status.category === "closed") {
    return t("statuses.closedIsSystem");
  }
  if (!canArchiveByCategory) {
    return status.category === "not_started"
      ? t("statuses.notStartedKeepsOne")
      : t("statuses.activeKeepsOne");
  }
  return null;
}

function CreateStatusInline({
  category,
  disabled,
  onCreate,
}: {
  category: BoardStatusCategory;
  disabled: boolean;
  onCreate: (name: string) => Promise<void>;
}) {
  const t = useT();
  async function createStatus(name: string) {
    const nextName = name.trim();
    if (!nextName) {
      return;
    }
    await onCreate(nextName);
  }

  return (
    <div className="flex h-9 min-w-0 items-center gap-1.5 rounded-md px-1.5 text-muted-foreground transition-colors hover:bg-muted/15">
      <span className="size-5" />
      <Plus className="size-3 shrink-0" />
      <EditableText
        ariaLabel={t("a11y.newStatusName", { category })}
        className={cn("min-w-0 flex-1", disabled && "pointer-events-none opacity-50")}
        onChange={(name) => {
          void createStatus(name);
        }}
        placeholder={t("statuses.newStatus")}
        previewClassName="text-xs font-medium leading-5"
        submitMode="both"
        value=""
      />
    </div>
  );
}

function StatusColorPicker({
  disabled,
  onChange,
  value,
}: {
  disabled: boolean;
  onChange: (color: string) => void;
  value: string;
}) {
  const t = useT();
  return (
    <Popover>
      <PopoverTrigger
        render={
          <Button aria-label={t("statuses.changeColor")} className="rounded-md p-0" disabled={disabled} size="icon-xs" type="button" variant="ghost">
            <span className="size-3.5 rounded-sm border border-border/70" style={{ backgroundColor: value }} />
          </Button>
        }
      />
      <PopoverContent align="start" className="w-56">
        <PopoverHeader>
          <PopoverTitle>{t("statuses.statusColor")}</PopoverTitle>
        </PopoverHeader>
        <div className="grid grid-cols-6 gap-1.5">
          {boardStatusColorPresets.map((color) => (
            <button
              aria-label={t("a11y.pickColor", { color })}
              className="flex size-7 items-center justify-center rounded-md border border-border"
              key={color}
              onClick={() => onChange(color)}
              style={{ backgroundColor: color }}
              type="button"
            >
              {color.toLowerCase() === value.toLowerCase() ? <Check className="text-white" /> : null}
            </button>
          ))}
        </div>
        <label className="flex items-center justify-between gap-2 text-xs font-medium">
          {t("statuses.custom")}
          <Input
            aria-label={t("statuses.customColor")}
            className="h-8 w-24 p-1"
            onChange={(event) => onChange(event.target.value)}
            type="color"
            value={value}
          />
        </label>
      </PopoverContent>
    </Popover>
  );
}

function statusesForCategory(statuses: BoardStatusDefinition[], category: BoardStatusCategory) {
  return statuses
    .filter((status) => status.category === category)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

